import { useEffect, useMemo, useState } from "react"

import { useEvent } from "shared/lib/useEvent"

import { LogoSliderProps } from "../LogoSlider"
import { LogoSliderViewModel } from "../ui/LogoSliderView"

const LOGO_WIDTH = 120
const LOGO_GAP = 32

export function useLogoSliderViewModel(
  args: LogoSliderProps
): LogoSliderViewModel {
  const { logoCount, logoGap = LOGO_GAP, logoWidth = LOGO_WIDTH } = args
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(
    null
  )
  const [scrollContainerWidth, setScrollContainerWidth] = useState(0)

  const requiresScroll = useMemo(() => {
    return (
      logoCount * logoWidth + Math.max(logoCount - 1, 0) * logoGap >
      scrollContainerWidth
    )
  }, [logoCount, scrollContainerWidth])

  useEffect(() => {
    if (!scrollContainer) return undefined

    const resizeObserver = new ResizeObserver(entries => {
      // eslint-disable-next-line no-restricted-syntax
      for (const entry of entries) {
        const { width } = entry.contentRect
        setScrollContainerWidth(width)
      }
    })

    // Initial measurement
    setScrollContainerWidth(scrollContainer.getBoundingClientRect().width)

    resizeObserver.observe(scrollContainer)

    // Cleanup
    return () => {
      resizeObserver.disconnect()
    }
  }, [scrollContainer, setScrollContainerWidth])

  const scrollToNextPartial = useEvent(() => {
    if (!scrollContainer) return

    const { scrollLeft } = scrollContainer
    const itemWidth = logoWidth + logoGap

    const lastVisibleIndex = Math.floor(
      (scrollLeft + scrollContainerWidth) / itemWidth
    )

    // Check if there's a partially visible item on the right
    const rightEdge = scrollLeft + scrollContainerWidth
    const lastItemEnd = (lastVisibleIndex + 1) * itemWidth - logoGap

    if (rightEdge < lastItemEnd) {
      // Scroll to make the partially visible item fully visible
      scrollContainer.scrollTo({
        left:
          (lastVisibleIndex + 1) * itemWidth -
          logoGap / 2 -
          scrollContainerWidth,
        behavior: "smooth"
      })
    } else {
      // No partial item, scroll by one full item
      scrollContainer.scrollTo({
        left: scrollLeft + itemWidth,
        behavior: "smooth"
      })
    }
  })

  const scrollToPrevPartial = useEvent(() => {
    if (!scrollContainer) return

    const { scrollLeft } = scrollContainer
    const itemWidth = logoWidth + logoGap

    // Calculate which items are currently visible
    const firstVisibleIndex = Math.floor(scrollLeft / itemWidth)

    // Check if there's a partially visible item on the left
    const firstItemStart = firstVisibleIndex * itemWidth

    if (scrollLeft > firstItemStart) {
      // Scroll to make the partially visible item fully visible
      scrollContainer.scrollTo({
        left: firstItemStart - logoGap / 2,
        behavior: "smooth"
      })
    } else {
      // No partial item, scroll by one full item
      scrollContainer.scrollTo({
        left: Math.max(0, scrollLeft - itemWidth),
        behavior: "smooth"
      })
    }
  })

  return {
    ...args,
    scrollContainer,
    setScrollContainer,
    requiresScroll,
    onScrollNext: scrollToNextPartial,
    onScrollPrev: scrollToPrevPartial
  }
}
